/**
 * pushNotifications.ts
 *
 * Handles registration, permission, FCM/APNs token management,
 * and routing of incoming notifications to the correct Vue route.
 *
 * Flow:
 *   1. App starts → initPushNotifications()
 *   2. Request permission (iOS prompts, Android 13+ prompts)
 *   3. On registration → send FCM token to backend
 *   4. On notification tap → navigate to correct screen
 */
import { isNative } from './native.js'
import { getAuthHeaders } from './api.js'

const API_URL = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '')

// ── Initialise ────────────────────────────────────────────────────────────────

export async function initPushNotifications(router: any): Promise<void> {
  if (!isNative()) return

  const { PushNotifications } = await import('@capacitor/push-notifications')

  // Check / request permission
  let permStatus = await PushNotifications.checkPermissions()
  if (permStatus.receive === 'prompt') {
    permStatus = await PushNotifications.requestPermissions()
  }
  if (permStatus.receive !== 'granted') {
    console.warn('[Push] Permission denied')
    return
  }

  // Register with APNs / FCM
  await PushNotifications.register()

  // ── Listeners ────────────────────────────────────────────────────────────────

  // Token received — send to backend so we can target this device
  await PushNotifications.addListener('registration', async token => {
    console.info('[Push] Token:', token.value)
    await registerTokenWithBackend(token.value)
  })

  await PushNotifications.addListener('registrationError', err => {
    console.error('[Push] Registration error:', err.error)
  })

  // Notification received while app is open — show local notification
  await PushNotifications.addListener('pushNotificationReceived', notification => {
    console.log('[Push] Received in foreground:', notification)
    // Capacitor shows it natively because we set presentationOptions in config
  })

  // User tapped a notification — navigate to the relevant screen
  await PushNotifications.addListener('pushNotificationActionPerformed', async action => {
    const data = action.notification.data as Record<string, string>
    console.log('[Push] Action performed:', data)
    await routeFromNotification(data, router)
  })
}

// ── Send token to backend ─────────────────────────────────────────────────────

async function registerTokenWithBackend(token: string): Promise<void> {
  try {
    await fetch(`${API_URL}/api/users/push-token`, {
      method:      'POST',
      headers:     { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body:        JSON.stringify({ token, platform: getPlatform() }),
    })
  } catch (err) {
    console.warn('[Push] Failed to register token with backend:', err)
  }
}

// ── Route from notification data ──────────────────────────────────────────────

async function routeFromNotification(
  data: Record<string, string>,
  router: any
): Promise<void> {
  const { type, ticketId, appealId } = data

  switch (type) {
    case 'deadline_reminder':
    case 'council_response_due':
      if (ticketId) await router.push(`/tickets/${ticketId}`)
      break
    case 'popla_available':
      if (ticketId) await router.push(`/tickets/${ticketId}/popla`)
      break
    default:
      if (ticketId) await router.push(`/tickets/${ticketId}`)
      else await router.push('/')
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getPlatform(): string {
  if (typeof window === 'undefined') return 'unknown'
  return /android/i.test(navigator.userAgent) ? 'android' : 'ios'
}

// ── Remove token on logout ────────────────────────────────────────────────────

export async function unregisterPushToken(): Promise<void> {
  if (!isNative()) return
  try {
    await fetch(`${API_URL}/api/users/push-token`, {
      method:      'DELETE',
      headers:     getAuthHeaders(),
      credentials: 'include',
    })
    const { PushNotifications } = await import('@capacitor/push-notifications')
    await PushNotifications.removeAllListeners()
  } catch {}
}
